import type { Language } from "@/hooks/useLanguage";

export type DpaLegal = {
  heroPill: string;
  vatStatus: string;
  title: string;
  subtitle: string;
  partiesTitle: string;
  controllerLabel: string;
  controllerBody: string;
  processorLabel: string;
  s1Title: string;
  s1p1: string;
  s1p2: string;
  s2Title: string;
  subjectLabel: string;
  subjectBody: string;
  durationLabel: string;
  durationBody: string;
  natureLabel: string;
  natureBody: string;
  s3Title: string;
  s31Title: string;
  s31Items: string[];
  s32Title: string;
  s32Items: string[];
  s4Title: string;
  s4Intro: string;
  s4FirstBullets: string[];
  s4MeasuresIntro: string;
  s4NestedMeasures: string[];
  s4LastBullets: string[];
  s5Title: string;
  s5Intro: string;
  thSub: string;
  thPurpose: string;
  thData: string;
  subRows: [string, string, string][];
  s5Footer: string;
  s6Title: string;
  s6p1: string;
  s6p2: string;
  s7Title: string;
  s7p1: string;
  s7Intro: string;
  s7Bullets: string[];
  s8Title: string;
  s8p1: string;
  s8p2: string;
  s9Title: string;
  s9p1: string;
  s9p2: string;
  s10Title: string;
  s10p1: string;
  s11Title: string;
  s11p1Prefix: string;
  termsLinkLabel: string;
  s11p2Rest: string;
  closingDate: string;
};

const cs: DpaLegal = {
  vatStatus: "neplátce DPH",
  heroPill:
    "Smlouva o zpracování osobních údajů · zveřejněno 23. října 2026 · pro stávající Zákazníky od 15. listopadu 2026",
  title: "Smlouva o zpracování osobních údajů",
  subtitle: "uzavřená dle čl. 28 nařízení Evropského parlamentu a Rady (EU) 2016/679 (GDPR)",
  partiesTitle: "Smluvní strany",
  controllerLabel: "Správce",
  controllerBody:
    "Zákazník služby Zettl — podnikatel, který s Poskytovatelem uzavřel smlouvu o poskytování Služby podle Obchodních podmínek.",
  processorLabel: "Zpracovatel",
  s1Title: "1. Předmět smlouvy",
  s1p1: "Tato Smlouva upravuje práva a povinnosti Smluvních stran při zpracování osobních údajů Zpracovatelem jménem Správce v rámci poskytování služeb platformy Zettl.",
  s1p2: "Zpracovatel zpracovává osobní údaje výhradně na základě doložených pokynů Správce, a to i s ohledem na předávání údajů do třetích zemí (čl. 6).",
  s2Title: "2. Předmět a doba zpracování",
  subjectLabel: "Předmět zpracování:",
  subjectBody:
    "poskytování SaaS služeb fakturace, správy účtenek, bankovní synchronizace, rozpočtování, AI zpracování dokladů, správy klientů a týmového přístupu.",
  durationLabel: "Doba zpracování:",
  durationBody:
    "po dobu trvání smlouvy o poskytování Služby a dále po dobu uchování dat podle čl. 10 Obchodních podmínek (12 měsíců v režimu pouze pro čtení po skončení předplatného; po smazání účtu nejvýše 30 dnů, ze záloh do dalších 30 dnů). Soubory, které již nejsou přiřazeny k žádnému záznamu, Zpracovatel odstraní do 30 dnů na žádost Správce zaslanou na info@idex-corp.com.",
  natureLabel: "Povaha zpracování:",
  natureBody: "automatizované i manuální zpracování, ukládání, šifrování, zálohování, mazání.",
  s3Title: "3. Kategorie subjektů údajů a osobních údajů",
  s31Title: "3.1 Kategorie subjektů údajů",
  s31Items: [
    "Uživatelé Platformy (zaměstnanci, OSVČ, členové organizace Správce)",
    "Klienti Správce (kontaktní osoby firem vedených v databázi klientů)",
  ],
  s32Title: "3.2 Kategorie osobních údajů",
  s32Items: [
    "Identifikační údaje (jméno, e-mail, název firmy, IČO, DIČ)",
    "Kontaktní údaje (adresa, telefon)",
    "Finanční údaje (transakce, faktury, účtenky, rozpočty)",
    "Technické údaje (IP adresy, user agent)",
    "Přístupové údaje k externím službám (šifrované tokeny)",
  ],
  s4Title: "4. Povinnosti zpracovatele",
  s4Intro: "Zpracovatel se zavazuje:",
  s4FirstBullets: [
    "Zpracovávat osobní údaje pouze na základě doložených pokynů Správce",
    "Zajistit, aby osoby oprávněné zpracovávat osobní údaje byly vázány povinností mlčenlivosti",
  ],
  s4MeasuresIntro: "Přijmout veškerá technická a organizační opatření dle čl. 32 GDPR:",
  s4LastBullets: [
    "Nezapojit dalšího zpracovatele bez předchozího písemného souhlasu Správce (obecný souhlas je udělen pro sub-zpracovatele uvedené v čl. 5)",
    "Pomáhat Správci při plnění jeho povinností vůči subjektům údajů (práva dle čl. 15–22 GDPR)",
    "Po ukončení zpracování smazat osobní údaje v databázi a soubory dokladů a log, které Služba u Správce eviduje, nebo je vrátit Správci (dle volby Správce); soubory, které již nejsou přiřazeny k žádnému záznamu (například u dříve smazaných dokladů), odstranit do 30 dnů na žádost Správce zaslanou na info@idex-corp.com",
    "Poskytnout Správci veškeré informace potřebné k doložení souladu s čl. 28 GDPR a umožnit audit",
  ],
  s4NestedMeasures: [
    "šifrování AES-GCM na úrovni polí (PBKDF2 derivace klíčů)",
    "HTTPS/TLS pro přenos",
    "PostgreSQL Row Level Security",
    "CAPTCHA na přihlášení a omezení počtu požadavků (rate limiting)",
    "denní šifrované zálohy uchovávané 30 dní",
    "kompletní auditní záznamy",
  ],
  s5Title: "5. Sub-zpracovatelé",
  s5Intro: "Správce uděluje obecný souhlas se zapojením následujících sub-zpracovatelů:",
  thSub: "Sub-zpracovatel",
  thPurpose: "Účel",
  thData: "Sídlo dat",
  subRows: [
    ["Supabase, Inc.", "Databáze, přihlášení, úložiště souborů, serverové funkce", "EU (Irsko)"],
    ["Vercel Inc.", "Hosting, CDN, AI Gateway", "EU/USA"],
    [
      "Google LLC (Gemini)",
      "Čtení dokladů a párování pomocí AI (přes Vercel AI Gateway)",
      "EU/USA",
    ],
    ["Cloudflare, Inc.", "Příjem e-mailů s doklady; šifrované noční zálohy (R2)", "EU/USA"],
    ["Resend, Inc.", "Odesílání e-mailů (pozvánky do týmu)", "EU/USA"],
    ["Functional Software, Inc. (Sentry)", "Sledování chyb aplikace", "EU (Německo)"],
    ["GitHub, Inc.", "Spouštění noční zálohy (data se před uložením šifrují)", "USA"],
    ["Stripe Payments Europe, Ltd.", "Platby za předplatné (od spuštění plateb)", "EU (Irsko)/USA"],
  ],
  s5Footer:
    "O zamýšlené změně sub-zpracovatelů bude Správce informován e-mailem nejméně 14 dní předem. Správce může proti změně vznést námitku do 14 dnů od oznámení.",
  s6Title: "6. Předávání údajů mimo EU",
  s6p1: "Databáze a nahrané soubory jsou uloženy v EU (Irsko), chybová hlášení v EU (Německo). U sub-zpracovatelů označených „EU/USA“ nebo „USA“ v čl. 5, a u sub-zpracovatelů se sídlem v USA i tehdy, když jsou data uložena v EU, však může docházet k předávání osobních údajů do třetích zemí mimo Evropský hospodářský prostor. Správce Zpracovateli k tomuto předávání uděluje pokyn.",
  s6p2: "Zpracovatel předávání zajišťuje na základě rozhodnutí Evropské komise o odpovídající ochraně (EU-US Data Privacy Framework) u certifikovaných příjemců, jinak na základě standardních smluvních doložek (čl. 46 GDPR).",
  s7Title: "7. Oznámení o porušení zabezpečení",
  s7p1: "Zpracovatel oznámí Správci jakékoli porušení zabezpečení osobních údajů bez zbytečného odkladu, nejpozději do 48 hodin od zjištění.",
  s7Intro: "Oznámení bude obsahovat:",
  s7Bullets: [
    "Popis povahy porušení",
    "Kategorie a přibližný počet dotčených subjektů",
    "Pravděpodobné důsledky a přijatá opatření",
  ],
  s8Title: "8. Audit",
  s8p1: "Zpracovatel poskytne Správci na vyžádání veškeré informace potřebné k prokázání souladu s povinnostmi dle čl. 28 GDPR.",
  s8p2: "Správce nebo jím pověřený auditor je oprávněn provést audit za předpokladu, že o něm informuje Zpracovatele nejméně 30 dní předem a audit nebude nepřiměřeně narušovat provoz Zpracovatele.",
  s9Title: "9. Doba trvání a ukončení",
  s9p1: "Tato Smlouva je účinná po dobu, po kterou Zpracovatel pro Správce zpracovává osobní údaje, tj. po dobu trvání smlouvy o poskytování Služby a uchování dat podle čl. 10 Obchodních podmínek.",
  s9p2: "Po skončení zpracování Zpracovatel osobní údaje smaže ve lhůtách podle čl. 10 Obchodních podmínek, ledaže právní předpisy vyžadují delší uchování. Správce si je předtím může kdykoli exportovat.",
  s10Title: "10. Rozhodné právo",
  s10p1:
    "Tato Smlouva se řídí právním řádem České republiky a GDPR. Spory budou řešeny příslušnými soudy v Praze.",
  s11Title: "11. Závěrečná ustanovení",
  s11p1Prefix: "Tato Smlouva je nedílnou součástí ",
  termsLinkLabel: "Obchodních podmínek služby Zettl",
  s11p2Rest:
    "V případě rozporu mezi touto Smlouvou a Obchodními podmínkami má v otázkách ochrany osobních údajů přednost tato Smlouva. Toto znění se na smlouvy uzavřené po jeho zveřejnění dne 23. října 2026 vztahuje ode dne jejich uzavření, na dříve uzavřené smlouvy ode dne 15. listopadu 2026; do té doby se na ně vztahuje Smlouva o zpracování osobních údajů účinná od 21. března 2026.",
  closingDate: "V Praze dne 23. října 2026",
};

const en: DpaLegal = {
  vatStatus: "not a VAT payer",
  heroPill:
    "Data Processing Agreement · published 23 October 2026 · for existing Customers from 15 November 2026",
  title: "Data Processing Agreement",
  subtitle: "concluded pursuant to Art. 28 of Regulation (EU) 2016/679 (GDPR)",
  partiesTitle: "Parties",
  controllerLabel: "Controller",
  controllerBody:
    "Customer of the Zettl service — a business that has concluded a contract for the Service with the Provider under the Terms of Service.",
  processorLabel: "Processor",
  s1Title: "1. Subject matter",
  s1p1: "This Agreement governs the parties’ rights and obligations concerning the processing of personal data by the Processor on behalf of the Controller in connection with the Zettl platform services.",
  s1p2: "The Processor processes personal data solely on documented instructions from the Controller, including with regard to transfers to third countries (Section 6).",
  s2Title: "2. Subject matter and duration of processing",
  subjectLabel: "Subject matter:",
  subjectBody:
    "provision of SaaS services for invoicing, receipt management, bank synchronisation, budgeting, AI document processing, client management, and team access.",
  durationLabel: "Duration:",
  durationBody:
    "for the duration of the contract for the Service and then for the data retention period under Article 10 of the Terms of Service (12 months in read-only mode after the subscription ends; at most 30 days after account deletion, and from backups within a further 30 days). Files no longer linked to any record are removed within 30 days of the Controller's request sent to info@idex-corp.com.",
  natureLabel: "Nature of processing:",
  natureBody: "automated and manual processing, storage, encryption, backup, and deletion.",
  s3Title: "3. Categories of data subjects and personal data",
  s31Title: "3.1 Categories of data subjects",
  s31Items: [
    "Platform users (employees, sole traders, members of the Controller’s organisation)",
    "Controller’s clients (contact persons of companies in the client database)",
  ],
  s32Title: "3.2 Categories of personal data",
  s32Items: [
    "Identification data (name, email, company name, company ID, VAT ID)",
    "Contact data (address, phone)",
    "Financial data (transactions, invoices, receipts, budgets)",
    "Technical data (IP addresses, user agent)",
    "Credentials for external services (encrypted tokens)",
  ],
  s4Title: "4. Processor obligations",
  s4Intro: "The Processor undertakes to:",
  s4FirstBullets: [
    "Process personal data only on documented instructions from the Controller",
    "Ensure that persons authorised to process personal data are bound by confidentiality",
  ],
  s4MeasuresIntro:
    "Implement all technical and organisational measures required under Art. 32 GDPR:",
  s4LastBullets: [
    "Not engage another processor without prior written consent of the Controller (general consent is granted for subprocessors listed in clause 5)",
    "Assist the Controller in fulfilling obligations towards data subjects (rights under Arts. 15–22 GDPR)",
    "After processing ends, delete the personal data in the database and the document and logo files the Service holds for the Controller, or return them to the Controller (at the Controller’s choice); remove files no longer linked to any record (for example of documents deleted earlier) within 30 days of the Controller's request sent to info@idex-corp.com",
    "Provide the Controller with all information necessary to demonstrate compliance with Art. 28 GDPR and permit audits",
  ],
  s4NestedMeasures: [
    "AES-GCM field-level encryption (PBKDF2 key derivation)",
    "HTTPS/TLS in transit",
    "PostgreSQL Row Level Security",
    "CAPTCHA on sign-in and rate limiting",
    "daily encrypted backups kept for 30 days",
    "full audit logging",
  ],
  s5Title: "5. Sub-processors",
  s5Intro: "The Controller grants general authorisation to engage the following sub-processors:",
  thSub: "Sub-processor",
  thPurpose: "Purpose",
  thData: "Data location",
  subRows: [
    ["Supabase, Inc.", "Database, sign-in, file storage, server functions", "EU (Ireland)"],
    ["Vercel Inc.", "Hosting, CDN, AI Gateway", "EU/USA"],
    ["Google LLC (Gemini)", "AI document reading and matching (via Vercel AI Gateway)", "EU/USA"],
    [
      "Cloudflare, Inc.",
      "Receiving e-mails with documents; encrypted nightly backups (R2)",
      "EU/USA",
    ],
    ["Resend, Inc.", "Sending e-mails (team invitations)", "EU/USA"],
    ["Functional Software, Inc. (Sentry)", "Application error monitoring", "EU (Germany)"],
    ["GitHub, Inc.", "Running the nightly backup (data is encrypted before storage)", "USA"],
    [
      "Stripe Payments Europe, Ltd.",
      "Subscription payments (once payments launch)",
      "EU (Ireland)/USA",
    ],
  ],
  s5Footer:
    "The Controller will be informed by email of intended changes to sub-processors at least 14 days in advance. The Controller may object within 14 days of notification.",
  s6Title: "6. Transfers outside the EU",
  s6p1: "The database and uploaded files are stored in the EU (Ireland), error reports in the EU (Germany). Sub-processors marked “EU/USA” or “USA” in Section 5, and US-based sub-processors even where data is stored in the EU, may, however, transfer personal data to third countries outside the European Economic Area. The Controller instructs the Processor to make these transfers.",
  s6p2: "The Processor bases these transfers on the European Commission's adequacy decision (EU-US Data Privacy Framework) for certified recipients and otherwise on standard contractual clauses (Art. 46 GDPR).",
  s7Title: "7. Security breach notification",
  s7p1: "The Processor will notify the Controller of any personal data breach without undue delay and no later than 48 hours after becoming aware.",
  s7Intro: "The notification will include:",
  s7Bullets: [
    "A description of the nature of the breach",
    "Categories and approximate number of data subjects affected",
    "Likely consequences and measures taken",
  ],
  s8Title: "8. Audit",
  s8p1: "Upon request, the Processor will provide the Controller with all information necessary to demonstrate compliance with obligations under Art. 28 GDPR.",
  s8p2: "The Controller or an auditor appointed by the Controller may conduct an audit provided the Processor is informed at least 30 days in advance and the audit does not unreasonably disrupt the Processor’s operations.",
  s9Title: "9. Term and termination",
  s9p1: "This Agreement remains in effect for as long as the Processor processes personal data for the Controller, i.e. for the duration of the contract for the Service and the data retention period under Article 10 of the Terms of Service.",
  s9p2: "When processing ends, the Processor deletes the personal data within the periods under Article 10 of the Terms of Service, unless the law requires longer retention. The Controller may export the data at any time before then.",
  s10Title: "10. Governing law",
  s10p1:
    "This Agreement is governed by the laws of the Czech Republic and the GDPR. Disputes shall be resolved by the competent courts in Prague.",
  s11Title: "11. Final provisions",
  s11p1Prefix: "This Agreement forms an integral part of the ",
  termsLinkLabel: "Zettl Terms of Service",
  s11p2Rest:
    "In case of conflict between this Agreement and the Terms of Service, this Agreement prevails on matters of personal data protection. This version applies to contracts concluded after its publication on 23 October 2026 from their conclusion, and to earlier contracts from 15 November 2026; until then the Data Processing Agreement effective from 21 March 2026 applies to them.",
  closingDate: "Prague, 23 October 2026",
};

export const dpaTranslations: Record<Language, DpaLegal> = {
  cs,
  en,
};
